import { BASIC_POLL_STATUSES, BASIC_POLL_VOTE_TYPES } from "./constants";
import { computeMultipleChoiceTallies } from "./multiple-choice";
import { computeInstantRunoffResults } from "./irv";

export const RESULTS_VIEW_MODES = Object.freeze({
  HIDDEN: "hidden",
  PROGRESS_ONLY: "progress-only",
  LIVE_RESULTS: "live-results",
  CLOSED_RESULTS: "closed-results",
  FINAL_RESULTS: "final-results",
});

function resolveStatus(poll) {
  const status = poll?.status;
  if (status === BASIC_POLL_STATUSES.FINALIZED) return BASIC_POLL_STATUSES.FINALIZED;
  if (status === BASIC_POLL_STATUSES.CLOSED) return BASIC_POLL_STATUSES.CLOSED;
  return BASIC_POLL_STATUSES.OPEN;
}

function resolveVoteType(poll) {
  const voteType = poll?.settings?.voteType;
  return voteType === BASIC_POLL_VOTE_TYPES.RANKED_CHOICE
    ? BASIC_POLL_VOTE_TYPES.RANKED_CHOICE
    : BASIC_POLL_VOTE_TYPES.MULTIPLE_CHOICE;
}

// Derive a results snapshot from the loaded vote docs, matching the shape the
// server persists as `finalResults` (see functions/src/legacy.js
// buildBasicPollFinalResultsSnapshot). Counts-only — no voter identity lists.
// `submittedVotes` is expected to already be filtered to submitted ballots.
function deriveMultipleChoiceResults(poll, submittedVotes) {
  const settings = poll?.settings || {};
  const allowWriteIn = settings.allowWriteIn === true;
  const tallies = computeMultipleChoiceTallies({
    options: Array.isArray(poll?.options) ? poll.options : [],
    votes: submittedVotes,
    allowWriteIn,
  });
  const rows = (tallies.rows || []).map((row) => ({
    key: row.key,
    label: row.label,
    order: row.order,
    count: row.count,
    percentage: row.percentage,
  }));
  const winningCount = rows.reduce((max, row) => Math.max(max, row.count), 0);
  const winnerIds = winningCount > 0 ? rows.filter((row) => row.count === winningCount).map((row) => row.key) : [];
  return {
    voteType: BASIC_POLL_VOTE_TYPES.MULTIPLE_CHOICE,
    rows,
    winnerIds,
    voterCount: Number.isFinite(tallies.totalVoters) ? tallies.totalVoters : submittedVotes.length,
  };
}

function deriveRankedChoiceResults(poll, submittedVotes) {
  const options = Array.isArray(poll?.options) ? poll.options : [];
  const irv = computeInstantRunoffResults({
    optionIds: options.map((option) => option?.id).filter(Boolean),
    votes: submittedVotes,
  });
  const rounds = Array.isArray(irv.rounds) ? irv.rounds : [];
  const lastRound = rounds.length > 0 ? rounds[rounds.length - 1] : null;
  return {
    voteType: BASIC_POLL_VOTE_TYPES.RANKED_CHOICE,
    rounds,
    winnerIds: Array.isArray(irv.winnerIds) ? irv.winnerIds : [],
    tiedIds: Array.isArray(irv.tiedIds) ? irv.tiedIds : [],
    voterCount: Number.isFinite(irv.totalBallots) ? irv.totalBallots : submittedVotes.length,
    exhaustedCount: Number.isFinite(lastRound?.exhausted) ? lastRound.exhausted : 0,
  };
}

// Pure results view model. THE PRIVACY GATE: a tally derived from `votes` is
// ONLY returned when `canReadVoteDetails` (selections visible) is true — never
// when only `canReadVoteProgress` (who-voted/identities) is unlocked. This is
// the fail-loud guardrail the unit test in results-view-model.test.js pins.
//
// Modes:
//  - "hidden"          viewer may not see selections at all → no tally
//  - "progress-only"   viewer sees progress/identities but NOT selections → no tally
//  - "live-results"    OPEN poll, viewer entitled → tally derived from votes
//  - "closed-results"  no longer accepting votes (persisted CLOSED, lapsed
//                      deadline, or cancelled parent) but not finalized → same
//                      derived tally, headed "Results so far" so it does not
//                      imply live-updating
//  - "final-results"   FINALIZED, viewer entitled → prefer poll.finalResults, else derive
//
// `isClosed` is caller-supplied so this function stays PURE (no Date.now() or
// deadline re-parsing here — the card already derived closed-ness, and a second
// Date.now() read could straddle the deadline and skew the label vs the badge).
export function getResultsViewModel({
  poll,
  votes = [],
  canReadVoteDetails = false,
  canReadVoteProgress = false,
  isClosed = false,
} = {}) {
  if (!poll) {
    return { mode: RESULTS_VIEW_MODES.HIDDEN, results: null };
  }

  // PRIVACY GATE — selections-gated, not progress-gated.
  if (!canReadVoteDetails) {
    return {
      mode: canReadVoteProgress
        ? RESULTS_VIEW_MODES.PROGRESS_ONLY
        : RESULTS_VIEW_MODES.HIDDEN,
      results: null,
    };
  }

  const status = resolveStatus(poll);
  const voteType = resolveVoteType(poll);
  const settings = poll?.settings || {};
  const allowWriteIn =
    voteType === BASIC_POLL_VOTE_TYPES.MULTIPLE_CHOICE && settings.allowWriteIn === true;

  const submittedVotes = (votes || []).filter((voteDoc) =>
    isSubmittedBallot(voteType, allowWriteIn, voteDoc)
  );

  const derived =
    voteType === BASIC_POLL_VOTE_TYPES.RANKED_CHOICE
      ? deriveRankedChoiceResults(poll, submittedVotes)
      : deriveMultipleChoiceResults(poll, submittedVotes);

  if (status === BASIC_POLL_STATUSES.FINALIZED) {
    const persisted = poll.finalResults;
    if (persisted && persisted.voteType === voteType) {
      return { mode: RESULTS_VIEW_MODES.FINAL_RESULTS, results: persisted };
    }
    return { mode: RESULTS_VIEW_MODES.FINAL_RESULTS, results: derived };
  }

  // OPEN (or CLOSED) — live/closed tally for entitled viewers. A poll that is
  // no longer accepting votes — persisted CLOSED, or caller-signalled closed
  // (lapsed deadline / cancelled parent via `isClosed`) — renders the same
  // derived distribution as CLOSED_RESULTS so the heading reads "Results so
  // far" instead of implying live-updating "Live results". Callers that omit
  // `isClosed` on a persisted-OPEN poll keep LIVE_RESULTS (back-compat).
  const showClosed = isClosed === true || status === BASIC_POLL_STATUSES.CLOSED;
  return {
    mode: showClosed ? RESULTS_VIEW_MODES.CLOSED_RESULTS : RESULTS_VIEW_MODES.LIVE_RESULTS,
    results: derived,
  };
}

function isSubmittedBallot(voteType, allowWriteIn, voteDoc) {
  if (!voteDoc) return false;
  if (voteType === BASIC_POLL_VOTE_TYPES.RANKED_CHOICE) {
    return Array.isArray(voteDoc.rankings) && voteDoc.rankings.some((id) => typeof id === "string" && id.trim());
  }
  const hasOptionIds =
    Array.isArray(voteDoc.optionIds) && voteDoc.optionIds.some((id) => typeof id === "string" && id.trim());
  const hasWriteIn = Boolean(allowWriteIn) && String(voteDoc.otherText || "").trim().length > 0;
  return Boolean(hasOptionIds || hasWriteIn);
}
